/**
 * Floating cart / sticky ATC bar: shown when the primary add-to-cart leaves the viewport
 * and the storefront footer is not in view (avoids covering the footer).
 */

const ATC_SELECTOR = 'button.add-to-cart-submit[type="submit"]';

/**
 * Main storefront footer (avoids matching unrelated <footer> if any).
 * @returns {HTMLElement | null}
 */
function findSiteFooter() {
  return document.querySelector("footer[aria-label]") || document.querySelector("footer");
}

/**
 * First ATC button in the document that is not inside this floating bar (avoids observing our own duplicate).
 * @param {HTMLElement} host
 * @returns {HTMLButtonElement | null}
 */
function findPrimaryAtcButton(host) {
  const buttons = document.querySelectorAll(ATC_SELECTOR);
  for (let i = 0; i < buttons.length; i += 1) {
    const btn = buttons[i];
    if (!(btn instanceof HTMLButtonElement)) continue;
    if (host.contains(btn)) continue;
    return btn;
  }
  return null;
}

class FloatingCartBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #atcIo = null;

  /** @type {IntersectionObserver | null} */
  #footerIo = null;

  /** @type {boolean} */
  #primaryInView = true;

  /** @type {boolean} */
  #footerInView = false;

  /** @type {HTMLElement | null} */
  #panel = null;

  connectedCallback() {
    this.#panel = this.querySelector("[data-floating-cart-panel]");
    if (!this.#panel) return;

    this.#reparentToViewport();

    this.#panel.classList.remove("is-visible");
    this.#panel.setAttribute("aria-hidden", "true");

    this.#bindAtc();
    this.#bindFooter();
  }

  disconnectedCallback() {
    this.#atcIo?.disconnect();
    this.#atcIo = null;
    this.#footerIo?.disconnect();
    this.#footerIo = null;
  }

  /**
   * Fixed positioning is relative to the viewport unless an ancestor has transform / filter / etc.
   * Product sections use sticky + animation wrappers, so we attach this host to document.body.
   */
  #reparentToViewport() {
    if (this.dataset.reparent === "false") return;
    if (typeof window.Shopify !== "undefined" && window.Shopify.designMode) return;

    if (this.parentElement === document.body) return;

    this.style.display = "contents";
    document.body.appendChild(this);
  }

  #syncPanelVisibility() {
    const panel = this.#panel;
    if (!panel) return;
    const showFloating = !this.#primaryInView && !this.#footerInView;
    panel.classList.toggle("is-visible", showFloating);
    panel.setAttribute("aria-hidden", showFloating ? "false" : "true");
  }

  #bindAtc() {
    this.#atcIo?.disconnect();

    const trigger = findPrimaryAtcButton(this);
    if (!trigger || !this.#panel) {
      return;
    }

    this.#atcIo = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.#primaryInView = entry.isIntersecting;
        this.#syncPanelVisibility();
      },
      {
        root: null,
        rootMargin: "0px 0px 12px 0px",
        threshold: 0,
      },
    );

    this.#atcIo.observe(trigger);
  }

  #bindFooter() {
    this.#footerIo?.disconnect();

    const footer = findSiteFooter();
    if (!footer || !this.#panel) return;

    this.#footerIo = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.#footerInView = entry.isIntersecting;
        this.#syncPanelVisibility();
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0,
      },
    );

    this.#footerIo.observe(footer);
  }
}

if (!customElements.get("floating-cart-bar")) {
  customElements.define("floating-cart-bar", FloatingCartBar);
}
